<div class="print-container">
    <!-- Header -->
    <div class="border-bottom border-2 pb-2 mb-3">
        <div class="row g-0">
            <div class="col-8">
                <h5 class="fw-bold mb-2">{{ booking.name }}</h5>
                <div class="small">
                    <span class="me-3">
                        <strong>Date:</strong> {{ booking.start | dateTime : venue.timeZone : DateTime.DATETIME_SHORT }}
                    </span>
                    <span class="me-3">
                        <strong>Party:</strong> {{ booking.size }} {{ booking.size === 1 ? 'guest' : 'guests' }}
                    </span>
                    @if (booking.phoneNumber) {
                        <span>
                            <strong>Phone:</strong> {{ booking.phoneNumber }}
                        </span>
                    }
                </div>
                @if (menu) {
                    <div class="small mt-1">
                        <strong>Menu:</strong> {{ menu.displayName }}
                    </div>
                }
            </div>
            <div class="col-4 text-end">
                <div class="small text-muted">{{ booking.docket ? 'Pre-Order' : 'Upsell Order' }}</div>
            </div>
        </div>
    </div>

    @if (!booking.order && !booking.docket) {
        <div class="text-center py-4 text-muted">
            No order information available for this booking.
        </div>
    } @else if (booking.order && !orderData) {
        <!-- Upsell Order -->
        @if (booking.notes) {
            <div class="mb-3">
                <div class="border rounded p-2">
                    <div class="small fw-semibold text-uppercase mb-1">Customer Notes</div>
                    <div class="small">{{ booking.notes }}</div>
                </div>
            </div>
        }
        <div class="mb-3">
            <div class="fw-semibold text-uppercase small border-bottom border-2 pb-1 mb-2">Upsell Order</div>
            <table class="table table-sm mb-0">
                <thead>
                    <tr>
                        <th class="py-1 px-2 text-center" style="width: 50px;">Qty</th>
                        <th class="py-1 px-2">Item</th>
                        @if (booking.order.isPaid) {
                            <th class="py-1 px-2 text-end">Price</th>
                            <th class="py-1 px-2 text-end">Total</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    @for (item of booking.order.lineItems; track item.id) {
                        <tr>
                            <td class="py-1 px-2 text-center fw-bold">{{ item.quantity }}</td>
                            <td class="py-1 px-2">
                                <span class="fw-medium">{{ item.name }}</span>
                                @if (item.modifiers && item.modifiers.length > 0) {
                                    <span class="small text-muted"> — {{ getModifierNames(item.modifiers) }}</span>
                                }
                            </td>
                            @if (booking.order.isPaid) {
                                <td class="py-1 px-2 text-end">@if (item.paidOnline) { {{ item.unitAmount() / 100 | currency:booking.order.currencyCode }} }</td>
                                <td class="py-1 px-2 text-end">@if (item.paidOnline) { {{ item.unitAmount() * item.quantity / 100 | currency:booking.order.currencyCode }} }</td>
                            }
                        </tr>
                    }
                </tbody>
            </table>
            @if (booking.order.isPaid) {
                <p class="text-end fw-semibold">Total: {{ booking.order.total() / 100 | currency:booking.order.currencyCode }}</p>
            }
            @if (booking.order.message) { <p>Order note: {{ booking.order.message }}</p> }
        </div>
    } @else if (orderData) {
        <!-- Notes -->
        @if (booking.notes) {
            <div class="mb-3">
                <div class="border rounded p-2">
                    <div class="small fw-semibold text-uppercase mb-1">Customer Notes</div>
                    <div class="small">{{ booking.notes }}</div>
                </div>
            </div>
        }

        <!-- Course Summary -->
        @if (orderData.courseSummaries.length > 0) {
            <div class="mb-3">
                <div class="fw-semibold text-uppercase small border-bottom border-2 pb-1 mb-2">Summary</div>
                <table class="table table-sm mb-0">
                    <thead>
                        <tr>
                            <th class="py-1 px-2 text-center" style="width: 50px;">Qty</th>
                            <th class="py-1 px-2">Item</th>
                            <th class="py-1 px-2">Course</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (course of orderData.courseSummaries; track $index) {
                            @for (item of course.items; track $index) {
                                <tr>
                                    <td class="py-1 px-2 text-center fw-bold">{{ item.quantity }}</td>
                                    <td class="py-1 px-2">
                                        <span class="fw-medium">{{ item.productName }}</span>
                                        @if (item.modifierNames.length > 0) {
                                            <span class="small text-muted"> — {{ item.modifierNames.join(', ') }}</span>
                                        }
                                    </td>
                                    <td class="py-1 px-2 text-muted">{{ course.courseName }}</td>
                                </tr>
                            }
                        }
                    </tbody>
                </table>
            </div>
        }

        <!-- Per-Cover Details -->
        @if (orderData.rows.length > 0 && orderData.courseNames.length > 0) {
            <div>
                <div class="fw-semibold text-uppercase small border-bottom border-2 pb-1 mb-2">Guest Selections</div>
                <table class="table table-sm mb-0">
                    <thead>
                        <tr>
                            <th class="py-1 px-2" style="width: 100px;">Guest</th>
                            @for (course of orderData.courseNames; track $index) {
                                <th class="py-1 px-2">{{ course }}</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of orderData.rows; track $index) {
                            <tr class="align-top">
                                <td class="py-2 px-2">
                                    <div class="fw-semibold">{{ row.name }}</div>
                                    @if (row.notes) {
                                        <div class="small fst-italic mt-1">{{ row.notes }}</div>
                                    }
                                </td>
                                @for (course of orderData.courseNames; track $index) {
                                    <td class="py-2 px-2">
                                        @for (item of getSelectionsForCourse(row, course); track $index; let last = $last) {
                                            <div [class.mb-1]="!last">{{ item }}</div>
                                        }
                                        @if (getSelectionsForCourse(row, course).length === 0) {
                                            <span class="text-muted">—</span>
                                        }
                                    </td>
                                }
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        }
    }
</div>
